import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { showToast } from "@/lib/toast";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { ParticipantList } from "@/components/memo/ParticipantList";
import { UserMultiSelect } from "@/components/memo/UserMultiSelect";
import type { MemoMutations } from "@/hooks/useMemo";
import type { Memo, MemoUser } from "@/lib/types";

/**
 * Who was in the room — readable by everyone, editable by admins.
 *
 * 🔴 It used to be admin-ONLY, and not because attendance is secret: the dialog is an
 * EDITOR (a UserMultiSelect and a save button), so opening it for a participant would hand
 * them a form whose save the backend rejects — `PATCH /memos/{id}/participants` calls
 * `require_admin`. So the chip in the header was simply made inert for everyone else, and it
 * still read "10 Teilnehmer", so it LOOKED clickable and did nothing.
 *
 * A participant has every right to see who else was there — it is their own meeting. So the
 * dialog now has two faces. `canEdit` false: the roster, read-only. `canEdit` true: today's
 * editor, unchanged. The backend guard is untouched and remains the real boundary; this only
 * stops the UI promising an action it cannot deliver.
 */
export function MemoParticipantsDialog({
  open,
  onOpenChange,
  memo,
  mut,
  canEdit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  memo: Memo;
  mut: MemoMutations;
  canEdit: boolean;
}) {
  const [people, setPeople] = useState<MemoUser[]>(memo.participants);
  useEffect(() => {
    if (open) setPeople(memo.participants);
  }, [open, memo]);
  const save = () =>
    mut.setParticipants.mutate(
      people.map((u) => u.id),
      {
        onSuccess: () => {
          showToast(t.memoParticipantsUpdated, "success");
          onOpenChange(false);
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );

  if (!canEdit) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange} title={t.memoParticipantsTitle} width={480}>
        <div style={{ marginTop: 6 }}>
          <ParticipantList people={memo.participants} />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t.memoParticipantsEdit} width={480}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 4 }}>
        <UserMultiSelect value={people} onChange={setPeople} />
        <button
          type="button"
          onClick={save}
          disabled={mut.setParticipants.isPending}
          style={{
            alignSelf: "flex-end",
            background: BRAND.blue,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "9px 16px",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {t.memoParticipantsEdit}
        </button>
      </div>
    </Dialog>
  );
}
